import * as types from '@types'
import * as services from '@services/contact'
import { resolveCustomFieldValues } from '@convenia/common-organisms/SystemFields/helpers'

export default {
  state: {
    address: { },
    contact: { },
    emergency: [
      { id: new Date().getUTCMilliseconds() }
    ],
    options: {},
    customFields: {}
  },

  getters: { },

  mutations: {
    [types.EMPLOYEE_ADDRESS]: (state, address) => {
      state.address = { ...address }
    },

    [types.EMPLOYEE_CONTACTS]: (state, contact) => {
      state.contact = { ...contact }
    },

    [types.EMPLOYEE_EMERGENCY_CONTACT_ADD]: (state) => {
      state.emergency = [
        ...state.emergency,
        { id: new Date().getUTCMilliseconds() }
      ]
    },

    [types.EMPLOYEE_EMERGENCY_CONTACT_SET]: (state, emergency) => {
      state.emergency = [ ...emergency ]
    },

    [types.EMPLOYEE_EMERGENCY_CONTACTS]: (state, emergency) => {
      state.emergency = [ ...emergency || [] ]

      if (!state.emergency.length)
        state.emergency = [
          { id: new Date().getUTCMilliseconds() }
        ]
    },

    [types.EMPLOYEE_EMERGENCY_CONTACT_DELETE]: (state, emergency) => {
      state.emergency = state.emergency.filter(({ id }) => id !== emergency.id)
    },

    [types.EMPLOYEE_ADDRESS_CONTACTS_OPTIONS]: (state, options) => {
      state.options = { ...options }
    },

    [types.EMPLOYEE_ADDRESS_CUSTOM_FIELDS]: (state, data) => {
      state.customFields = { ...data }

      const reduceValues = (acc, [ id, field ]) => ({ [id]: field.value, ...acc })

      const addressValues = Object.entries(data.address?.schema || {}).reduce(reduceValues, {})
      const contactValues = Object.entries(data.contact?.schema || {}).reduce(reduceValues, {})

      state.address = { ...state.address, ...addressValues }
      state.contact = { ...state.contact, ...contactValues }

      state.emergency = resolveCustomFieldValues(
        state.emergency,
        state.customFields?.emergencyContact
      )
    }
  },

  actions: {
    [types.EMPLOYEE_ADDRESS_CONTACTS_OPTIONS]: async ({ commit }) => {
      const [ err, options ] = await services.getOptions()

      if (!err) commit(types.EMPLOYEE_ADDRESS_CONTACTS_OPTIONS, options)

      return [ err, options ]
    },

    [types.EMPLOYEE_ADDRESS]: async ({ commit, getters }) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const params = { employeeId, companyId }

      const [ err, data ] = await services.getAddress(params)

      if (!err) commit(types.EMPLOYEE_ADDRESS, data)

      return [ err, data ]
    },

    [types.EMPLOYEE_CONTACTS]: async ({ commit, getters }) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const params = { employeeId, companyId }

      const [ err, data ] = await services.getContacts(params)

      if (!err) commit(types.EMPLOYEE_CONTACTS, data)

      return [ err, data ]
    },
    [types.EMPLOYEE_EMERGENCY_CONTACTS]: async ({ commit, getters }) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const params = { employeeId, companyId }

      const [ err, data ] = await services.getEmergencyContacts(params)

      if (!err) commit(types.EMPLOYEE_EMERGENCY_CONTACTS, data)

      return [ err, data ]
    },

    [types.EMPLOYEE_ADDRESS_CUSTOM_FIELDS]: async ({ commit, getters }) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const params = { employeeId, companyId }

      const [ err, data ] = await services.getCustomFields(params)

      if (!err) commit(types.EMPLOYEE_ADDRESS_CUSTOM_FIELDS, data)

      return [ err, data ]
    },

    [types.EMPLOYEE_ADDRESS_SET]: async ({ state, getters, commit }, data) => {
      const { schema } = state.customFields.address || {}
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const params = { employeeId, companyId, schema }

      const [ err, address ] = await services.updateAddress(data, params)

      if (!err) commit(types.EMPLOYEE_ADDRESS, address)

      return [ err, address ]
    },

    [types.EMPLOYEE_CONTACT_SET]: async ({ state, getters, commit }, data) => {
      const { schema } = state.customFields.contact || {}
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const params = { employeeId, companyId, schema }

      const [ err, contact ] = await services.updateContact(data, params)

      if (!err) commit(types.EMPLOYEE_CONTACTS, contact)

      return [ err, contact ]
    },

    [types.EMPLOYEE_EMERGENCY_CONTACT_SET]: async ({ state, getters }, contacts) => {
      const { schema } = state?.customFields.emergencyContact || {}
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const params = { employeeId, companyId, schema }

      const submitted = []
      const errors = []

      const results = await Promise.allSettled((contacts || [])
        .map(data => (typeof data?.id) === 'string'
          ? services.updateEmergencyContact(data, params)
          : services.createEmergencyContact(data, params)
        )
      ).catch(err => errors.push(err))

      results.forEach(({ value: [ err, contact ] = [] }) => err
        ? errors.push(err)
        : submitted.push(contact)
      )

      return [ errors, submitted ]
    },

    [types.EMPLOYEE_EMERGENCY_CONTACT_DELETE]: async (store, data) => {
      const { getters, commit, state } = store
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const params = { employeeId, companyId }

      const [ err ] = await services.deleteEmergencyContact(data, params)

      if (!err) commit(types.EMPLOYEE_EMERGENCY_CONTACT_DELETE, data)

      const hasNoEmergencyContacts = !state.emergency?.length

      if (hasNoEmergencyContacts) commit(types.EMPLOYEE_EMERGENCY_CONTACT_ADD)

      return [ err, null ]
    },

    [types.EMPLOYEE_ADDRESS_UPLOAD_FILES]: async ({ getters, dispatch }, { id, files }) => {
      const employeeId = getters.getEmployeeUuid
      const params = { id, files, employeeId }

      const [ err ] = await services.uploadAddressFiles(params)

      if (err) return [ err, null ]

      return dispatch(types.EMPLOYEE_ADDRESS)
    },
  }
}
